import React, { useMemo } from 'react';
import {
  BrowserRouter,
  Redirect,
  Route,
  RouteProps,
  Switch
} from 'react-router-dom';
import HomeContainer from '@routes/Home';
import SignInContainer from '@routes/SignIn/SignInContainer';
import SignUpContainer from '@routes/SignUp/SignUpContainer';
import RequestContainer from '@routes/NewRequest/NewRequestContainer';
import Dashboard from '@routes/Dashboard';
import ServiceAndTermsContainer from '@routes/ServiceAndTerms/ServiceAndTermsContainer';
import NotFoundPresenter from '@routes/NotFound';
import ResetPasswordContainer from '@routes/SignIn/ResetPasswordContainer';
import RequestPasswordContainer from '@routes/SignIn/RequestPasswordContainer';
import RequestProcessMethodContainer from '@routes/RequestProcessMethod';
import MyAccountContainer from '@routes/MyAccount';
import AccountSwitchContainer from '@routes/Auth/AccountSwitchContainer';
import Blog from '@routes/Blog';
import ClipBoard from '@routes/ClipBoard';
import ConnectServicePresenter from '@routes/ConnectService/ConnectServicePresenter';
import UpdateNoticeModalContainer from '@routes/Common/Dialog/UpdateNoticeModal';
import { IUpdateModalState } from './useNoticeSchedule';
import ChannelIoProvider from '@components/Provider/ChannelIoProvider';
import UpdatedNoticeDialog from '@components/Dialog/Notice/UpdatedNoticeDialog';
import CompanyInfoContainer from '@routes/CompanyInfo';
import { CapaViewTutorial } from '@routes/CapaViewTutorial';
import jwtDecode from '@utils/jwtDecode';
import { useSelector } from '@store';
import Payment from '@routes/Payment';
import CapaUpdateDialog from '@components/Dialog/Notice/CapaUpdateDialog';
import GTMWrapper from './GTMWrapper';
import Viewer from '@routes/Viewer/Viewer';
import ServiceAgreeDialog from '@components/Dialog/ServiceAgreeDialog/ServiceAgreeDialog';
import useProcess from '@hooks/useProcess';
import ServicePolicyDialog from '@components/Dialog/ServicePolicyDialog/ServicePolicyDialog';

interface ExtendedRouteProps extends RouteProps {
  restrict?: boolean;
}

const ExtendedRoute = ({ restrict = false, ...props }: ExtendedRouteProps) => {
  const accessToken = useSelector((state) => state.client.accessToken);

  const isAuth = useMemo(() => {
    const decode = jwtDecode();

    if (!decode) return false;

    return true;

    //eslint-disable-next-line
  }, [accessToken]);

  if (restrict) {
    return isAuth ? <Route {...props} /> : <Redirect to="/login" exact />;
  }

  return <Route {...props} />;
};

const Routes = () => {
  useProcess();

  return (
    <Switch>
      <ExtendedRoute path="/" exact={true} component={HomeContainer} />
      <ExtendedRoute path="/login" exact={true} component={SignInContainer} />
      <ExtendedRoute
        path="/login/resetpassword"
        exact={true}
        component={ResetPasswordContainer}
      />
      <ExtendedRoute
        path="/login/requestpassword"
        exact={true}
        component={RequestPasswordContainer}
      />
      <ExtendedRoute
        path={'/signup'}
        exact={true}
        component={SignUpContainer}
      />
      <ExtendedRoute
        path={'/signup/continue/:token'}
        exact={true}
        component={SignUpContainer}
      />
      <ExtendedRoute
        path="/signup/referral/:id"
        exact={true}
        component={SignUpContainer}
      />
      <ExtendedRoute
        exact={true}
        component={SignUpContainer}
        path={['/signup/complete', '/signup/complete/:id']}
      />
      <ExtendedRoute
        path="/signup/friends/:capaFriendsId"
        exact={true}
        component={SignUpContainer}
      />
      <ExtendedRoute
        exact={true}
        component={AccountSwitchContainer}
        path={'/auth'}
      />
      <ExtendedRoute
        path={'/request'}
        exact={true}
        component={RequestContainer}
      />
      <ExtendedRoute
        path={'/processmethod'}
        exact={true}
        component={RequestProcessMethodContainer}
      />
      {/* <Route path={'/request'} exact={true} component={RequestForQuotation} /> */}
      <ExtendedRoute
        path={'/request'}
        exact={true}
        component={RequestContainer}
      />
      <ExtendedRoute
        restrict
        path={['/dashboard', '/dashboard/:rfqId', '/chat', '/chat/:chatRoomId']}
        exact={true}
        component={Dashboard}
      />

      <ExtendedRoute
        exact={true}
        path={['/clipboard/quotation', '/clipboard/request']}
        component={ClipBoard}
      />
      {/* Dashboard End */}

      <ExtendedRoute
        exact
        path={['/terms', '/terms/:urlParam']}
        component={ServiceAndTermsContainer}
      />
      <ExtendedRoute
        restrict
        path={[
          '/mypage',
          '/mypage/:urlParam',
          '/mypage/:urlParam/:detail',
          '/mypage/withdrawal-complete'
        ]}
        exact={true}
        component={MyAccountContainer}
      />

      <ExtendedRoute
        exact={true}
        path={[
          '/blog',
          '/blog/post/:slug',
          '/blog/posts/:slug',
          '/blog/posts',
          '/blog/category/:mainCategory',
          '/blog/category/:mainCategory/:subCategory',
          '/blog/tag/:tagName'
        ]}
        component={Blog}
      />

      <ExtendedRoute
        exact={true}
        path={['/companyInfo']}
        component={CompanyInfoContainer}
      />

      <ExtendedRoute exact={true} path={['/payment']} component={Payment} />

      <ExtendedRoute
        path="/connectservice"
        component={ConnectServicePresenter}
      />

      <ExtendedRoute
        path={['/capaviewtutorial', '/capaviewtutorial/:step']}
        exact={true}
        component={CapaViewTutorial}
      />

      <ExtendedRoute path="/viewer" exact={true} component={Viewer} />

      <ExtendedRoute path="*" component={NotFoundPresenter} />
    </Switch>
  );
};

interface AppPresenterProps {
  updateModalState: IUpdateModalState | null;
}

const AppPresenter = ({ updateModalState }: AppPresenterProps) => {
  return (
    <React.Fragment>
      <BrowserRouter>
        <ChannelIoProvider>
          <GTMWrapper>
            <Routes />
          </GTMWrapper>
        </ChannelIoProvider>
        <ServiceAgreeDialog />
        <ServicePolicyDialog />
        <UpdatedNoticeDialog />
        <CapaUpdateDialog />
      </BrowserRouter>
      {updateModalState && (
        <UpdateNoticeModalContainer updateModalState={updateModalState} />
      )}
    </React.Fragment>
  );
};

export default AppPresenter;
